import * as Phaser from 'phaser';
import { createLogger } from '../utils/Logger';
import { getSharedProfileRuntime, ProfileRuntime } from './ProfileRuntime';

interface FeedbackCue {
  readonly key: string;
  readonly volume?: number;
}

export class AudioManager {
  private isMuted = false;
  private musicVolume = 0.3;
  private sfxVolume = 0.7;
  private readonly logger = createLogger('AudioManager');
  private readonly declaredOptionalSfxKeys = new Set<string>();
  private suppressMissingAudioWarnings = false;
  private currentMusicKey: string | null = null;
  private currentMusicBaseVolume = 1;

  constructor(private readonly scene: Phaser.Scene, private readonly profileRuntime: ProfileRuntime = getSharedProfileRuntime()) {
    this.restoreState();
  }

  public playSfx(key: string, config: Phaser.Types.Sound.SoundConfig = {}): void {
    if (this.isMuted) return;
    if (!this.scene.cache.audio.exists(key)) {
      if (this.declaredOptionalSfxKeys.has(key)) {
        this.logMissingAudio('playSfx:missing-key', { key, declaredOptional: true });
      } else {
        this.logger.debug('playSfx:missing-key', { key, declaredOptional: false });
      }
      return;
    }

    const effectiveVolume = this.clampVolume((config.volume ?? 1) * this.sfxVolume);
    this.scene.sound.play(key, { ...config, volume: effectiveVolume });
    this.logger.debug('playSfx:dispatched', { key, effectiveVolume });
  }

  public playFeedbackCue(cue: FeedbackCue | undefined): void {
    if (!cue) return;
    this.playSfx(cue.key, { volume: cue.volume ?? 1 });
  }

  public playMusic(key: string, config: Phaser.Types.Sound.SoundConfig = {}): void {
    if (this.isMuted) return;
    this.scene.sound.stopAll();
    if (!this.scene.cache.audio.exists(key)) {
      this.logMissingAudio('playMusic:missing-key', { key });
      return;
    }

    const effectiveVolume = this.clampVolume((config.volume ?? 1) * this.musicVolume);
    this.currentMusicKey = key;
    this.currentMusicBaseVolume = this.clampVolume(config.volume ?? 1);
    this.scene.sound.play(key, { loop: true, ...config, volume: effectiveVolume });
    this.logger.info('playMusic:dispatched', { key, effectiveVolume });
  }

  public setMusicVolume(volume: number): void {
    this.musicVolume = this.clampVolume(volume);
    this.applyCurrentMusicVolume();
    this.persistState();
    this.logger.info('setMusicVolume:updated', { volume: this.musicVolume });
  }

  public setSfxVolume(volume: number): void {
    this.sfxVolume = this.clampVolume(volume);
    this.persistState();
    this.logger.info('setSfxVolume:updated', { volume: this.sfxVolume });
  }

  public toggleMute(): boolean {
    this.isMuted = !this.isMuted;
    this.scene.sound.mute = this.isMuted;
    this.persistState();
    this.logger.info('toggleMute:updated', { isMuted: this.isMuted });
    return this.isMuted;
  }

  public setMute(value: boolean): void {
    this.isMuted = value;
    this.scene.sound.mute = value;
    this.persistState();
    this.logger.info('setMute:updated', { isMuted: value });
  }

  public getMusicVolume(): number {
    return this.musicVolume;
  }

  public getSfxVolume(): number {
    return this.sfxVolume;
  }

  public getMute(): boolean {
    return this.isMuted;
  }

  public registerDeclaredOptionalSfxKeys(keys: readonly string[]): void {
    keys.forEach(key => {
      if (key && key.trim()) {
        this.declaredOptionalSfxKeys.add(key);
      }
    });
    this.logger.info('registerDeclaredOptionalSfxKeys:updated', { count: this.declaredOptionalSfxKeys.size });
  }

  public setMissingAudioWarningsSuppressed(suppressed: boolean): void {
    this.suppressMissingAudioWarnings = suppressed;
    this.logger.info('setMissingAudioWarningsSuppressed:updated', { suppressed });
  }

  private restoreState(): void {
    const snapshot = this.profileRuntime.getProfileSnapshot();
    if (!snapshot.ok) {
      this.logger.warn('restoreState:profile-unavailable-defaults-used', { error: snapshot.error });
      return;
    }

    this.musicVolume = this.clampVolume(snapshot.profile.settings.musicVolume);
    this.sfxVolume = this.clampVolume(snapshot.profile.settings.sfxVolume);
    this.isMuted = snapshot.profile.settings.mute;
    this.scene.sound.mute = this.isMuted;
    this.logger.debug('restoreState:applied', { musicVolume: this.musicVolume, sfxVolume: this.sfxVolume, isMuted: this.isMuted });
  }

  private persistState(): void {
    const result = this.profileRuntime.updateSettings({
      musicVolume: this.musicVolume,
      sfxVolume: this.sfxVolume,
      mute: this.isMuted
    });
    if (!result.ok) {
      this.logger.warn('persistState:profile-write-unavailable', { error: result.error, reason: result.reason });
    }
  }

  private applyCurrentMusicVolume(): void {
    if (!this.currentMusicKey) {
      this.logger.debug('applyCurrentMusicVolume:skipped-no-active-music', { musicVolume: this.musicVolume });
      return;
    }

    const sound = this.scene.sound.get(this.currentMusicKey) as unknown as {
      setVolume?: (volume: number) => unknown;
      volume?: number;
    } | null;

    if (!sound) {
      this.logger.debug('applyCurrentMusicVolume:skipped-missing-active-sound', { key: this.currentMusicKey });
      return;
    }

    const effectiveVolume = this.clampVolume(this.currentMusicBaseVolume * this.musicVolume);
    if (typeof sound.setVolume === 'function') {
      sound.setVolume(effectiveVolume);
    } else {
      sound.volume = effectiveVolume;
    }
    this.logger.info('applyCurrentMusicVolume:active-music-updated', {
      key: this.currentMusicKey,
      musicVolume: this.musicVolume,
      effectiveVolume
    });
  }

  private logMissingAudio(message: string, payload: unknown): void {
    if (this.suppressMissingAudioWarnings) {
      this.logger.debug(message, { ...(payload as Record<string, unknown>), suppressed: true });
      return;
    }

    this.logger.warn(message, payload);
  }

  private clampVolume(volume: number): number {
    if (!Number.isFinite(volume)) {
      return 1;
    }
    return Math.min(1, Math.max(0, volume));
  }
}
